import type { Momento } from "@caint/engine";
/**
 * Fila durável de evidências capturadas offline (porta + adapter Dexie).
 * O arquivo (Blob) é guardado no IndexedDB; quando há rede, a fila é drenada
 * (presign → upload → confirmar). Offline-first: a foto não se perde.
 */
import Dexie, { type Table } from "dexie";

export interface EvidenciaPendente {
  localId: string;
  avaliacaoId: string;
  itemId: string;
  momento: Momento;
  contentType: string;
  blob: Blob;
  /** Miniatura gerada junto com a foto, para a galeria não baixar a imagem
   *  cheia. Opcional: capturas enfileiradas antes desta versão não têm, e PDF
   *  nunca tem. Campo novo NÃO indexado — o Dexie aceita sem migrar o store. */
  blobMiniatura?: Blob;
  criadoEm: number;
  /** Motivo pelo qual o SERVIDOR recusou esta captura (400/413 — arquivo grande
   *  demais, formato recusado). Preenchido, a drenagem PULA o item: reenviar
   *  daria o mesmo erro e travaria tudo o que veio depois. Recusa de SESSÃO
   *  (401/403) não entra aqui — não é defeito do arquivo. O arquivo continua
   *  guardado: quem cancela é a pessoa, vendo o motivo. Campo novo NÃO indexado
   *  — o Dexie aceita sem migrar o store. */
  erro?: string;
}

/**
 * Exclusão de uma foto JÁ enviada ao servidor, capturada offline. Fica na fila
 * até drenar (DELETE /evidencias/:id); ao reconectar sobe sozinha, como as
 * capturas. Fotos ainda-pendentes (não enviadas) NÃO passam por aqui — basta
 * `remover(localId)` na fila de upload.
 */
export interface ExclusaoPendente {
  localId: string;
  evidenciaId: string;
  avaliacaoId: string;
  criadoEm: number;
}

/** Porta da fila — o app depende disto, não do Dexie diretamente (DIP). */
export interface EvidenciaQueue {
  enqueue(item: EvidenciaPendente): Promise<void>;
  todas(): Promise<EvidenciaPendente[]>;
  remover(localId: string): Promise<void>;
  /** Anota a recusa do servidor no pendente (ver `EvidenciaPendente.erro`). */
  marcarErro(localId: string, motivo: string): Promise<void>;
  enfileirarExclusao(item: ExclusaoPendente): Promise<void>;
  exclusoesPendentes(): Promise<ExclusaoPendente[]>;
  removerExclusao(localId: string): Promise<void>;
}

class EvidenciaDB extends Dexie {
  pendentes!: Table<EvidenciaPendente, string>;
  exclusoes!: Table<ExclusaoPendente, string>;
  constructor() {
    super("caint-evidencias");
    this.version(1).stores({ pendentes: "localId, itemId, avaliacaoId, criadoEm" });
    // v2: fila de exclusões de fotos já enviadas (novo store, sem migrar dados).
    this.version(2).stores({
      pendentes: "localId, itemId, avaliacaoId, criadoEm",
      exclusoes: "localId, evidenciaId, avaliacaoId, criadoEm",
    });
  }
}

export class DexieEvidenciaQueue implements EvidenciaQueue {
  private readonly db = new EvidenciaDB();

  async enqueue(item: EvidenciaPendente): Promise<void> {
    await this.db.pendentes.put(item);
  }

  async todas(): Promise<EvidenciaPendente[]> {
    return this.db.pendentes.orderBy("criadoEm").toArray();
  }

  async remover(localId: string): Promise<void> {
    await this.db.pendentes.delete(localId);
  }

  async marcarErro(localId: string, motivo: string): Promise<void> {
    await this.db.pendentes.update(localId, { erro: motivo });
  }

  async enfileirarExclusao(item: ExclusaoPendente): Promise<void> {
    await this.db.exclusoes.put(item);
  }

  async exclusoesPendentes(): Promise<ExclusaoPendente[]> {
    return this.db.exclusoes.orderBy("criadoEm").toArray();
  }

  async removerExclusao(localId: string): Promise<void> {
    await this.db.exclusoes.delete(localId);
  }
}
